import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';

const CARD_CONFIG = [
  { id: 'total', label: 'Total Orders', icon: 'ShoppingCart', iconBg: 'bg-blue-50', iconColor: '#2563eb', sub: 'vs last period', key: 'totalOrders', prevKey: 'prevTotalOrders', isPercent: false },
  { id: 'complete', label: 'Complete Address', icon: 'MapPin', iconBg: 'bg-green-50', iconColor: '#16a34a', sub: 'addresses verified', key: 'completePercent', volumeKey: 'complete', prevKey: 'prevComplete', prevTotalKey: 'prevTotalOrders', isPercent: true },
  { id: 'incomplete', label: 'Incomplete Address', icon: 'MapPinOff', iconBg: 'bg-red-50', iconColor: '#dc2626', sub: 'need attention', key: 'incompletePercent', volumeKey: 'incomplete', prevKey: 'prevIncomplete', prevTotalKey: 'prevTotalOrders', isPercent: true },
];

const ACMetricCards = ({ from, to, refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    api.get('/api/v1/address-confirmation/metrics', { params: { from, to } })
      .then((res) => {
        if (res.data?.success) setData(res.data.data);
      })
      .catch((err) => console.error('Failed to fetch AC metrics:', err))
      .finally(() => setLoading(false));
  }, [from, to, refreshKey]);

  const getTrend = (cfg) => {
    if (!data) return { trend: '', trendUp: true };
    if (!cfg.isPercent) {
      const curr = data[cfg.key] || 0;
      const prev = data[cfg.prevKey] || 0;
      if (prev === 0) return { trend: curr > 0 ? '+100%' : '0%', trendUp: curr >= prev };
      const diff = ((curr - prev) / prev * 100).toFixed(1);
      return { trend: `${diff > 0 ? '+' : ''}${diff}%`, trendUp: Number(diff) >= 0 };
    }
    const prevTotal = data[cfg.prevTotalKey] || 0;
    const prevCount = data[cfg.prevKey] || 0;
    const prevPct = prevTotal > 0 ? (prevCount / prevTotal * 100) : 0;
    const currPct = data[cfg.key] || 0;
    const diff = (currPct - prevPct).toFixed(1);
    const isGood = cfg.id === 'complete' ? Number(diff) >= 0 : Number(diff) <= 0;
    return { trend: `${diff > 0 ? '+' : ''}${diff}%`, trendUp: isGood };
  };

  const getValue = (cfg) => {
    if (!data) return { value: '—', volume: null };
    if (!cfg.isPercent) return { value: (data[cfg.key] || 0).toLocaleString(), volume: null };
    return {
      value: `${data[cfg.key] || 0}%`,
      volume: (data[cfg.volumeKey] || 0).toLocaleString(),
    };
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {CARD_CONFIG.map((cfg) => {
        const { value, volume } = getValue(cfg);
        const { trend, trendUp } = getTrend(cfg);

        return (
          <div
            key={cfg.id}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex flex-col gap-3 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${cfg.iconBg}`}>
                <Icon name={cfg.icon} size={20} color={cfg.iconColor} />
              </div>
              {!loading && data && (
                <span
                  className={`flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                    trendUp ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
                  }`}
                >
                  <Icon name={trendUp ? 'TrendingUp' : 'TrendingDown'} size={11} />
                  {trend}
                </span>
              )}
            </div>
            <div>
              {loading ? (
                <div className="h-8 w-20 bg-gray-100 rounded animate-pulse" />
              ) : (
                <>
                  <p className="text-xl md:text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                  {volume && (
                    <p className="text-sm font-semibold text-gray-500 leading-tight">{volume} orders</p>
                  )}
                </>
              )}
              <p className="text-xs font-medium text-gray-500 mt-0.5">{cfg.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{cfg.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ACMetricCards;
